/**
 * Fallback runtime per i <video> con più <source>.
 * -------------------------------------------------------------------------
 * Il fallback nativo dei <source> copre solo il caso "type non supportato".
 * Safari iOS però risponde "maybe" a video/webm, seleziona il webm e poi
 * fallisce in DECODIFICA (VP9 senza hw decode) senza passare al source
 * successivo → video nero.
 *
 * Qui: se il video va in errore, o (per gli autoplay) non ha dati dopo un
 * timeout, rimuoviamo il <source> attivo e richiamiamo load() → il browser
 * riparte dalla catena rimasta (webm -> mp4 H.265 -> mp4 H.264).
 *
 * Si applica a TUTTI i video della pagina con almeno 2 source.
 */

const WATCHDOG_MS = 4000;

/**
 * Riordino proattivo dei source con canPlayType, PRIMA che parta il fetch:
 * "probably" > "maybe" > "" (ordine originale a parita' di rank, sort stabile).
 * - Safari: mp4 H.265 = "probably", webm = "maybe" → parte subito dall'mp4
 *   senza bruciare ~1s a provare (e fallire) il webm.
 * - Chrome: HEVC = "" → source rimosso subito, webm resta primo.
 * Ritorna true se la catena e' cambiata (serve un load()).
 */
function reorderSources(video) {
  const sources = Array.from(video.querySelectorAll('source'));
  if (sources.length < 2) return false;

  const rank = (s) => {
    const type = s.getAttribute('type') || '';
    if (!type) return 1; // senza type non possiamo giudicare: trattalo come "maybe"
    const support = video.canPlayType(type);
    if (support === 'probably') return 2;
    if (support === 'maybe') return 1;
    return 0; // non supportato
  };

  const ranked = sources.map((s, i) => ({ s, i, r: rank(s) }));
  // Tieni almeno un source anche se tutti a rank 0
  const playable = ranked.filter((x) => x.r > 0);
  const kept = playable.length ? playable : ranked;
  kept.sort((a, b) => b.r - a.r || a.i - b.i);

  const changed = kept.length !== sources.length || kept.some((x, pos) => x.s !== sources[pos]);
  if (!changed) return false;

  sources.forEach((s) => s.remove());
  kept.forEach((x) => video.appendChild(x.s));
  return true;
}

function setupVideoFallback(video) {
  const getSources = () => Array.from(video.querySelectorAll('source'));
  if (getSources().length < 2) return;

  // Riordina prima di tutto; se la catena e' cambiata, riparte la selezione.
  if (reorderSources(video) && video.paused) {
    video.load();
    if (video.autoplay) {
      const p = video.play();
      if (p && typeof p.catch === 'function') p.catch(() => {});
    }
  }

  let watchdog = null;

  const clear = () => {
    if (watchdog) {
      clearTimeout(watchdog);
      watchdog = null;
    }
  };

  const advance = () => {
    clear();
    const list = getSources();
    if (list.length < 2) return; // niente su cui ripiegare

    // Rimuovi il source attivo (quello fallito); se non identificabile, il primo.
    const current = list.find((s) => video.currentSrc && video.currentSrc === s.src) || list[0];
    current.remove();

    const wantsPlay = video.autoplay || !video.paused;
    video.load();
    if (wantsPlay) {
      const p = video.play();
      if (p && typeof p.catch === 'function') p.catch(() => {});
    }
    arm();
  };

  // Watchdog: solo per i video autoplay (quelli con controls partono su
  // input utente e un readyState basso e' normale finche' non si preme play).
  const arm = () => {
    clear();
    if (!(video.autoplay || !video.paused)) return;
    watchdog = setTimeout(() => {
      // HAVE_CURRENT_DATA (2) = almeno un frame decodificato
      if (video.error || video.readyState < 2) advance();
    }, WATCHDOG_MS);
  };

  video.addEventListener('error', advance);
  video.addEventListener('loadeddata', clear);
  video.addEventListener('playing', clear);
  // per i video con controls: arma il watchdog quando l'utente preme play
  video.addEventListener('play', arm);

  arm();
}

export function initVideoFallback(context = document) {
  context.querySelectorAll('video').forEach(setupVideoFallback);
}
